// Typed client for the two assessment endpoints. The widget talks ONLY to these
// — never to Foundry or HubSpot directly.

import type {
  Answer,
  AnyIntake,
  AssessmentMode,
  Attribution,
  Depth,
  FollowupResponse,
  ResumeResponse,
  StartResponse,
  SubmitResponse,
} from './types.js';

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function post<T>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError('Network error — please check your connection and try again.', 0);
  }
  if (!res.ok) {
    const detail = (await res.json().catch(() => null)) as { message?: string } | null;
    throw new ApiError(detail?.message ?? 'Something went wrong. Please try again.', res.status);
  }
  return (await res.json()) as T;
}

/** Kicks off the assessment; blocks ~15–30s while the site is fetched + analysed. */
export function startAssessment(
  intake: AnyIntake,
  depth: Depth,
  mode: AssessmentMode,
  attribution?: Attribution,
): Promise<StartResponse> {
  return post<StartResponse>('/api/assessment/start', { intake, depth, mode, attribution });
}

/** Deep mode: submit a round's answers; the agent decides if more are needed. */
export function followup(assessmentId: string, answers: Answer[]): Promise<FollowupResponse> {
  return post<FollowupResponse>('/api/assessment/followup', { assessmentId, answers });
}

export function submitAnswers(assessmentId: string, answers?: Answer[]): Promise<SubmitResponse> {
  return post<SubmitResponse>('/api/assessment/submit', { assessmentId, answers });
}

/** Rehydrates a saved assessment (from localStorage or an email ?a=<id> link). */
export async function getAssessment(assessmentId: string): Promise<ResumeResponse> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/assessment/${encodeURIComponent(assessmentId)}`);
  } catch {
    throw new ApiError('Network error — please check your connection and try again.', 0);
  }
  if (!res.ok) {
    const detail = (await res.json().catch(() => null)) as { message?: string } | null;
    throw new ApiError(detail?.message ?? 'Could not restore your assessment.', res.status);
  }
  return (await res.json()) as ResumeResponse;
}
